import Bugsnag from "@bugsnag/js";
import { supabase } from "@/lib/supabase";
import { CoachingWaitlist } from "@/types/coaching-waitlist";
import type { UserData } from "@/types/user-data";
import { normalizeEmailIdentity } from "@/lib/emailIdentity";
import { composeUserName } from "@/lib/personName";

class UserService {
  addUserToCoachingWaitlist = async (userData: Omit<CoachingWaitlist, "id" | "created_at" | "user_id">) => {
    const { data, error } = await supabase.from("coaching_waitlist").insert(userData).select().single();
    if (error) throw error;
    return data;
  };

  /**
   * Look up a user's last accepted Terms & Privacy effective dates by email.
   * SECURITY DEFINER RPC (`public.check_user_acceptance`) — safe to call pre-auth.
   * Returns null when no matching user row exists.
   * AE-175.
   */
  checkUserAcceptance = async (
    email: string,
  ): Promise<{ terms_effective_date: string | null; privacy_effective_date: string | null } | null> => {
    const { data, error } = await supabase.rpc("check_user_acceptance", { _email: normalizeEmailIdentity(email) });
    if (error) throw error;
    const row = (data ?? [])[0];
    if (!row) return null;
    return {
      terms_effective_date: row.terms_effective_date ?? null,
      privacy_effective_date: row.privacy_effective_date ?? null,
    };
  };

  /** Returns the AE-211 server-authoritative district routing scope for an email identity. */
  getDistrictScopeByEmail = async (email: string): Promise<string | null> => {
    const { data, error } = await supabase.rpc("check_district_by_email", {
      _email: normalizeEmailIdentity(email),
    });
    if (error) throw error;
    const row = (data ?? [])[0] as { domain?: string | null } | undefined;
    return row?.domain ?? null;
  };

  /**
   * Persist the current Terms & Privacy acceptance onto the `users` row for
   * the authenticated user. Caller is expected to invoke this AFTER
   * `supabase.auth.verifyOtp` succeeds so the user has a valid session.
   *
   * IMPORTANT: Existing users keep their existing `user_name`; new users get
   * the email local-part as a placeholder — OnboardingPage overwrites it.
   * AE-175. Fix AE-178: stop overwriting existing user_name on re-sign-in.
   */
  saveUserAcceptance = async (params: {
    userId: string;
    email: string;
    termsEffectiveDate: string;
    privacyEffectiveDate: string;
  }): Promise<void> => {
    const nowIso = new Date().toISOString();
    const identity = normalizeEmailIdentity(params.email);

    // First try update only the acceptance fields — avoids overwriting
    // an existing user_name with the email local-part (AE-178).
    const { data: existing, error: fetchError } = await supabase
      .from("users")
      .select("user_id")
      .eq("user_id", params.userId)
      .maybeSingle();

    if (fetchError) throw fetchError;

    if (existing) {
      // Row exists — update only acceptance fields, preserve user_name
      const { error } = await supabase
        .from("users")
        .update({
          terms_accepted_at: nowIso,
          terms_effective_date: params.termsEffectiveDate,
          privacy_accepted_at: nowIso,
          privacy_effective_date: params.privacyEffectiveDate,
        })
        .eq("user_id", params.userId);
      if (error) throw error;
    } else {
      // First sign-in — insert row with email local-part as placeholder;
      // OnboardingPage will overwrite user_name with the real name.
      const { error } = await supabase.from("users").insert({
        user_id: params.userId,
        email: identity,
        user_name: identity.split("@")[0] ?? identity,
        terms_accepted_at: nowIso,
        terms_effective_date: params.termsEffectiveDate,
        privacy_accepted_at: nowIso,
        privacy_effective_date: params.privacyEffectiveDate,
      });
      if (error) throw error;
    }
  };

  /**
   * Rename the authenticated user. AE-207.
   *
   * `first_name` / `last_name` are the source of truth; `user_name` is written alongside
   * them as the `"first last"` display value the sidebar, the hub greeting and the
   * `profiles` copy all read. The `users` write is authoritative and its failure is the
   * caller's to present.
   *
   * `profiles.user_name` is a denormalized display copy that `incidentService` joins on
   * for Restoration Room creator names, so it is synced best-effort — reported on
   * failure, never fatal, matching `accountOrgRequestService.linkProfileOrg`.
   */
  updateUserName = async (params: {
    userId: string;
    firstName: string;
    lastName?: string | null;
  }): Promise<UserData> => {
    const firstName = params.firstName.trim();
    const lastName = (params.lastName ?? "").trim();
    const userName = composeUserName(firstName, lastName);

    const { data, error } = await supabase
      .from("users")
      .update({ first_name: firstName, last_name: lastName || null, user_name: userName })
      .eq("user_id", params.userId)
      .select()
      .single();

    if (error) throw error;

    const { error: profileError } = await supabase
      .from("profiles")
      .update({ user_name: userName })
      .eq("id", params.userId);

    if (profileError) {
      // Callback form, matching `inviteTeammatesService.notifyUnexpectedInviteError` — the
      // object form used elsewhere in the repo does not typecheck against `OnErrorCallback`.
      Bugsnag.notify(new Error(profileError.message ?? "profiles user_name sync failed"), (event) => {
        event.severity = "warning";
        event.context = "userService.updateUserName";
      });
    }

    return data as UserData;
  };
}

export const userService = new UserService();
